// Import global utils
import { getFormattedDuration } from '@automator/shared/utils/get-formatted-duration';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _n, sprintf } from '@wordpress/i18n';

/**
 * Parses an input object to generate a human-readable sentence based on its mode.
 *
 * @param {Object} input - The object containing mode and related information.
 * @param {String} input.mode - The mode of the action ('delay' or 'schedule').
 * @param {String} [input.delayValue] - The numeric value for delay (required if mode is 'delay').
 * @param {String} [input.delayUnit] - The time unit for delay (required if mode is 'delay').
 * @param {String} [input.scheduleTime] - The time for scheduling (required if mode is 'schedule').
 * @param {String} [input.scheduleDate] - The date for scheduling (required if mode is 'schedule').
 * @return {String} A human-readable sentence describing the action.
 */
const getDelaySentence = input => {
	// Validate the input object
	if ( ! input || typeof input !== 'object' ) {
		throw new Error( 'Invalid input: Expected an object.' );
	}

	// Destructure the properties from the input
	const {
		MODE: mode,
		DELAY_VALUE: delayValue,
		DELAY_UNIT: delayUnit,
		SCHEDULE_TIME: scheduleTime,
		SCHEDULE_DATE: scheduleDate,
	} = input;

	switch ( mode ) {
		case 'delay': {
			if ( ! delayValue || ! delayUnit ) {
				throw new Error( 'Missing properties: delayValue and delayUnit are required for delay mode.' );
			}

			// Convert delayValue to a number
			const number = parseInt( delayValue );

			if ( isNaN( number ) ) {
				throw new Error( 'Invalid delayValue: Must be a numeric value.' );
			}

			// Get the singular or plural form of the unit
			const unitText = getFormattedDuration( delayUnit, number );

			return sprintf(
				escapeHTML( __( 'Run in %1$s', 'uncanny-automator' ) ),
				unitText
			);
		}

		case 'schedule': {
			if ( ! scheduleTime || ! scheduleDate ) {
				throw new Error( 'Missing properties: scheduleTime and scheduleDate are required for schedule mode.' );
			}

			// Build the human-readable sentence for scheduling
			return sprintf(
				escapeHTML( __( 'Run on %1$s @ %2$s', 'uncanny-automator' ) ),
				scheduleDate,
				scheduleTime
			);
		}

		default:
			throw new Error( `Invalid mode: ${ mode }` );
	}
};

/**
 * Returns the singular or plural form of a time unit using WordPress's i18n localization.
 * @param {String} unit - The unit of time (e.g., 'seconds', 'minutes', 'days').
 * @param {Number} number - The number to determine singular or plural form.
 * @return {String} The human-readable, localized singular or plural form of the unit.
 */
const getPluralUnit = ( unit, number ) => {
	switch ( unit ) {
		case 'seconds':
			return sprintf(
				escapeHTML( _n( '%1$s second', '%1$s seconds', number, 'uncanny-automator' ) ),
				number
			);

		case 'minutes':
			return sprintf(
				escapeHTML( _n( '%1$s minute', '%1$s minutes', number, 'uncanny-automator' ) ),
				number
			);

		case 'hours':
			return sprintf(
				escapeHTML( _n( '%1$s hour', '%1$s hours', number, 'uncanny-automator' ) ),
				number
			);

		case 'days':
			return sprintf(
				escapeHTML( _n( '%1$s day', '%1$s days', number, 'uncanny-automator' ) ),
				number
			);

		case 'weeks':
			return sprintf(
				escapeHTML( _n( '%1$s week', '%1$s weeks', number, 'uncanny-automator' ) ),
				number
			);

		case 'months':
			return sprintf(
				escapeHTML( _n( '%1$s month', '%1$s months', number, 'uncanny-automator' ) ),
				number
			);

		case 'years':
			return sprintf(
				escapeHTML( _n( '%1$s year', '%1$s years', number, 'uncanny-automator' ) ),
				number
			);

		default:
			throw new Error( `Invalid delayUnit: ${ unit }` );
	}
};

export default getDelaySentence;
